import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { C } from './icones';
import { libelleMenu } from '../../i18n/menu';

// Marge laissee avec les bords de la fenetre lors du recadrage vertical.
const MARGE_FENETRE = 8;
// Espace minimal que l'on tente de reserver sous le sommet du volet avant de
// le remonter : evite un volet ecrase a quelques pixels quand l'icone est
// tout en bas d'une petite fenetre.
const HAUTEUR_SOUHAITEE = 200;

/**
 * Volet affiche au survol ou au focus clavier d'une icone en mode compact.
 *
 * Rendu dans un portail vers document.body : l'aside de la sidebar a
 * overflowX 'hidden', un volet rendu a l'interieur serait decoupe.
 * La position vient donc du rectangle de l'element survole. Le
 * positionnement horizontal (ancre.right + 6) reste correct quel que soit le
 * defilement de la barre, car getBoundingClientRect renvoie des coordonnees
 * relatives a la fenetre - on n'y touche pas.
 */
export default function VoletCompact({ ancre, item, navigate, onEntrer, onFermer, onEchap, focaliserPremiereLigne }) {
  // Lignes reellement focalisables (tabIndex 0), dans l'ordre d'affichage.
  const lignesFocusables = useRef([]);

  // Focalise la premiere ligne uniquement a l'ouverture au clavier : a la
  // souris, voler le focus serait desagreable.
  useEffect(() => {
    if (ancre && focaliserPremiereLigne) {
      lignesFocusables.current[0]?.focus();
    }
  }, [ancre, focaliserPremiereLigne]);

  if (!ancre) return null;

  const enfants = item.children ?? [];

  lignesFocusables.current = [];
  const enregistrerLigne = (el) => { if (el) lignesFocusables.current.push(el); };

  // Enter/Espace active la ligne ; Echap ferme le volet et rend le focus a
  // l'icone d'origine, quelle que soit la ligne focalisee.
  const gererClavierLigne = (action) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onEchap();
    }
  };

  // Recadrage vertical : si la place sous l'ancre est insuffisante, on
  // remonte le sommet du volet (jamais au-dela de la marge haute). Le
  // maxHeight qui en decoule, combine a overflowY auto, garantit que le
  // volet reste entierement dans la fenetre et qu'aucune ligne ne devient
  // inatteignable, meme avec beaucoup d'enfants sur un petit ecran.
  const espaceSousAncre = window.innerHeight - MARGE_FENETRE - ancre.top;
  const top = espaceSousAncre >= HAUTEUR_SOUHAITEE
    ? ancre.top
    : Math.max(MARGE_FENETRE, window.innerHeight - MARGE_FENETRE - HAUTEUR_SOUHAITEE);
  const maxHeight = window.innerHeight - MARGE_FENETRE - top;

  return createPortal(
    <div
      // onEntrer annule la fermeture differee lancee par l'icone : sans lui,
      // le volet disparait pendant que la souris traverse l'espace qui l'en
      // separe, et ses enfants deviennent inatteignables a la souris. Le
      // focus/blur assure la meme chose au clavier (Tab d'une ligne a
      // l'autre a l'interieur du volet).
      onMouseEnter={onEntrer}
      onMouseLeave={onFermer}
      onFocus={onEntrer}
      onBlur={onFermer}
      style={{
        position: 'fixed', top, left: ancre.right + 6,
        minWidth: 200, maxHeight, overflowY: 'auto',
        background: C.bg1, borderRadius: 8, zIndex: 1100,
        boxShadow: '0 8px 24px rgba(0,0,0,0.35)', padding: 6,
        border: `1px solid ${C.border}`,
      }}
    >
      <div
        ref={item.path ? enregistrerLigne : undefined}
        tabIndex={item.path ? 0 : -1}
        onClick={() => { if (item.path) { navigate(item.path); onFermer(); } }}
        onKeyDown={item.path ? gererClavierLigne(() => { navigate(item.path); onFermer(); }) : undefined}
        style={{
          padding: '7px 10px', color: '#fff', fontSize: 16, fontWeight: 600,
          cursor: item.path ? 'pointer' : 'default', borderRadius: 6,
        }}
      >
        {libelleMenu(item)}
      </div>
      {enfants.map((enfant) => (
        <div
          key={enfant.cle}
          ref={enregistrerLigne}
          tabIndex={0}
          onClick={() => { navigate(enfant.path); onFermer(); }}
          onKeyDown={gererClavierLigne(() => { navigate(enfant.path); onFermer(); })}
          style={{
            padding: '6px 10px 6px 18px', color: '#fff',
            fontSize: 15, cursor: 'pointer', borderRadius: 6,
          }}
        >
          {libelleMenu(enfant)}
        </div>
      ))}
    </div>,
    document.body,
  );
}
